import { useState } from 'react';
import SEO from '../components/SEO';
import { api } from '../api';

const Admin = () => {
  const [formData, setFormData] = useState({
    make: '',
    model: '',
    year: new Date().getFullYear(),
    rangeKm: '',
    batterySize: '',
    batteryChemistry: '',
    batteryChemistryInfo: '',
    maxChargingSpeed: '',
    maxChargingSpeedInfo: '',
    drivetrain: 'AWD',
    wltpRange: '',
    power: '',
    weight: '',
    weather: 'sun',
    season: 'summer',
    temperature: '',
    tires: '',
    testDate: new Date().toISOString().split('T')[0],
    youtubeUrl: ''
  });

  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: '', message: '' });

    try {
      const dataToSubmit = {
        ...formData,
        year: parseInt(formData.year),
        rangeKm: parseInt(formData.rangeKm),
        batterySize: parseFloat(formData.batterySize),
        maxChargingSpeed: parseInt(formData.maxChargingSpeed),
        wltpRange: parseInt(formData.wltpRange),
        power: parseInt(formData.power),
        weight: parseInt(formData.weight),
        temperature: parseInt(formData.temperature),
        // Only include info fields if they have content
        batteryChemistryInfo: formData.batteryChemistryInfo?.trim() || undefined,
        maxChargingSpeedInfo: formData.maxChargingSpeedInfo?.trim() || undefined
      };

      const response = await api('/api/cars', {
        method: 'POST',
        body: JSON.stringify(dataToSubmit)
      });

      if (response.ok) {
        const result = await response.json();
        setStatus({
          type: 'success',
          message: `Successfully added ${formData.make} ${formData.model} (ID: ${result.car.id})!`
        });

        setFormData({
          make: '',
          model: '',
          year: new Date().getFullYear(),
          rangeKm: '',
          batterySize: '',
          batteryChemistry: '',
          batteryChemistryInfo: '',
          maxChargingSpeed: '',
          maxChargingSpeedInfo: '',
          drivetrain: 'AWD',
          wltpRange: '',
          power: '',
          weight: '',
          weather: 'sun',
          season: 'summer',
          temperature: '',
          tires: '',
          testDate: new Date().toISOString().split('T')[0],
          youtubeUrl: ''
        });

        window.scrollTo(0, 0);
      } else {
        const error = await response.json();
        setStatus({
          type: 'error',
          message: `Error: ${error.error}`
        });
      }
    } catch (error) {
      setStatus({
        type: 'error',
        message: `Error: ${error.message}. Make sure the admin server is running (npm run server).`
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-12 px-4">
      <SEO title="Admin" path="/admin" noindex />
      <div className="max-w-4xl mx-auto">
        <div className="bg-slate-800 rounded-2xl shadow-2xl p-8 border border-teal-500 border-opacity-30">
          <div className="mb-8">
            <h1 className="text-4xl font-black text-teal-400 mb-2">Admin Panel</h1>
            <p className="text-gray-300">Add new car range test results to the database</p>
          </div>

          <div className="flex gap-4 mb-6">
            <a
              href="/admin/meny"
              className="inline-block text-teal-400 hover:text-teal-300 font-semibold"
            >
              ← Admin-meny
            </a>
            <a
              href="/"
              className="inline-block text-teal-400 hover:text-teal-300 font-semibold"
            >
              Main Site
            </a>
            <a
              href="/manage"
              className="inline-block text-teal-400 hover:text-teal-300 font-semibold"
            >
              Manage Existing Tests →
            </a>
          </div>

          {status.message && (
            <div
              className={`mb-6 p-4 rounded-xl ${
                status.type === 'success'
                  ? 'bg-teal-500 bg-opacity-20 text-teal-300 border border-teal-500'
                  : 'bg-red-500 bg-opacity-20 text-red-300 border border-red-500'
              }`}
            >
              {status.message}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Vehicle Information */}
            <div className="bg-slate-700 bg-opacity-50 rounded-xl p-6 border border-teal-500 border-opacity-20">
              <h2 className="text-xl font-bold text-teal-400 mb-4">Vehicle Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">
                    Make *
                  </label>
                  <input
                    type="text"
                    name="make"
                    value={formData.make}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 bg-slate-900 border border-teal-500 border-opacity-30 text-gray-100 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    placeholder="Tesla"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">
                    Model *
                  </label>
                  <input
                    type="text"
                    name="model"
                    value={formData.model}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 bg-slate-900 border border-teal-500 border-opacity-30 text-gray-100 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    placeholder="Model S Long Range"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">
                    Year *
                  </label>
                  <input
                    type="number"
                    name="year"
                    value={formData.year}
                    onChange={handleChange}
                    required
                    min="2020"
                    max="2030"
                    className="w-full px-4 py-2 bg-slate-900 border border-teal-500 border-opacity-30 text-gray-100 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                </div>

              </div>
            </div>

            {/* Test Conditions */}
            <div className="bg-slate-700 bg-opacity-50 rounded-xl p-6 border border-teal-500 border-opacity-20">
              <h2 className="text-xl font-bold text-teal-400 mb-4">Test Conditions</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">
                    Weather *
                  </label>
                  <select
                    name="weather"
                    value={formData.weather}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 bg-slate-900 border border-teal-500 border-opacity-30 text-gray-100 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  >
                    <option value="sun">☀️ Sun</option>
                    <option value="rain">🌧️ Rain</option>
                    <option value="snow">❄️ Snow</option>
                    <option value="cloudy">☁️ Cloudy</option>
                    <option value="night">🌙 Night</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">
                    Season *
                  </label>
                  <select
                    name="season"
                    value={formData.season}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 bg-slate-900 border border-teal-500 border-opacity-30 text-gray-100 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  >
                    <option value="summer">☀️ Summer</option>
                    <option value="winter">❄️ Winter</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">
                    Temperature (°C) *
                  </label>
                  <input
                    type="number"
                    name="temperature"
                    value={formData.temperature}
                    onChange={handleChange}
                    required
                    min="-30"
                    max="50"
                    className="w-full px-4 py-2 bg-slate-900 border border-teal-500 border-opacity-30 text-gray-100 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    placeholder="24"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">
                    Test Date *
                  </label>
                  <input
                    type="date"
                    name="testDate"
                    value={formData.testDate}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 bg-slate-900 border border-teal-500 border-opacity-30 text-gray-100 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">
                    Tires *
                  </label>
                  <input
                    type="text"
                    name="tires"
                    value={formData.tires}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 bg-slate-900 border border-teal-500 border-opacity-30 text-gray-100 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    placeholder="Michelin Pilot Sport 4S"
                  />
                </div>
              </div>
            </div>

            {/* Test Results */}
            <div className="bg-slate-700 bg-opacity-50 rounded-xl p-6 border border-teal-500 border-opacity-20">
              <h2 className="text-xl font-bold text-teal-400 mb-4">Test Results</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">
                    Range (km) *
                  </label>
                  <input
                    type="number"
                    name="rangeKm"
                    value={formData.rangeKm}
                    onChange={handleChange}
                    required
                    min="0"
                    className="w-full px-4 py-2 bg-slate-900 border border-teal-500 border-opacity-30 text-gray-100 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    placeholder="652"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">
                    YouTube Video URL
                  </label>
                  <input
                    type="url"
                    name="youtubeUrl"
                    value={formData.youtubeUrl}
                    onChange={handleChange}
                    className="w-full px-4 py-2 bg-slate-900 border border-teal-500 border-opacity-30 text-gray-100 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    placeholder="https://www.youtube.com/watch?v=..."
                  />
                </div>
              </div>
            </div>

            {/* Vehicle Specs */}
            <div className="bg-slate-700 bg-opacity-50 rounded-xl p-6 border border-teal-500 border-opacity-20">
              <h2 className="text-xl font-bold text-teal-400 mb-4">Vehicle Specifications</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">
                    Battery Size (kWh) *
                  </label>
                  <input
                    type="number"
                    name="batterySize"
                    value={formData.batterySize}
                    onChange={handleChange}
                    required
                    step="0.1"
                    min="0"
                    className="w-full px-4 py-2 bg-slate-900 border border-teal-500 border-opacity-30 text-gray-100 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    placeholder="100"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">
                    Battery Chemistry *
                  </label>
                  <select
                    name="batteryChemistry"
                    value={formData.batteryChemistry}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 bg-slate-900 border border-teal-500 border-opacity-30 text-gray-100 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  >
                    <option value="">Select chemistry...</option>
                    <option value="NMC">NMC (Nickel Manganese Cobalt)</option>
                    <option value="LFP">LFP (Lithium Iron Phosphate)</option>
                    <option value="NCA">NCA (Nickel Cobalt Aluminum)</option>
                    <option value="LTO">LTO (Lithium Titanate)</option>
                    <option value="Solid State">Solid State</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">
                    Battery Chemistry Info (Optional)
                  </label>
                  <textarea
                    name="batteryChemistryInfo"
                    value={formData.batteryChemistryInfo}
                    onChange={handleChange}
                    rows="2"
                    className="w-full px-4 py-2 bg-slate-900 border border-teal-500 border-opacity-30 text-gray-100 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    placeholder="Additional info about battery chemistry..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">
                    Max Charging Speed (kW) *
                  </label>
                  <input
                    type="number"
                    name="maxChargingSpeed"
                    value={formData.maxChargingSpeed}
                    onChange={handleChange}
                    required
                    min="0"
                    className="w-full px-4 py-2 bg-slate-900 border border-teal-500 border-opacity-30 text-gray-100 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    placeholder="250"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">
                    Max Charging Speed Info (Optional)
                  </label>
                  <textarea
                    name="maxChargingSpeedInfo"
                    value={formData.maxChargingSpeedInfo}
                    onChange={handleChange}
                    rows="2"
                    className="w-full px-4 py-2 bg-slate-900 border border-teal-500 border-opacity-30 text-gray-100 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    placeholder="Additional info about charging speed..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">
                    Drivetrain *
                  </label>
                  <select
                    name="drivetrain"
                    value={formData.drivetrain}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 bg-slate-900 border border-teal-500 border-opacity-30 text-gray-100 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  >
                    <option value="AWD">AWD</option>
                    <option value="RWD">RWD</option>
                    <option value="FWD">FWD</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">
                    WLTP Range (km) *
                  </label>
                  <input
                    type="number"
                    name="wltpRange"
                    value={formData.wltpRange}
                    onChange={handleChange}
                    required
                    min="0"
                    className="w-full px-4 py-2 bg-slate-900 border border-teal-500 border-opacity-30 text-gray-100 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    placeholder="634"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">
                    Power (hp) *
                  </label>
                  <input
                    type="number"
                    name="power"
                    value={formData.power}
                    onChange={handleChange}
                    required
                    min="0"
                    className="w-full px-4 py-2 bg-slate-900 border border-teal-500 border-opacity-30 text-gray-100 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    placeholder="670"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">
                    Weight (kg) *
                  </label>
                  <input
                    type="number"
                    name="weight"
                    value={formData.weight}
                    onChange={handleChange}
                    required
                    min="0"
                    className="w-full px-4 py-2 bg-slate-900 border border-teal-500 border-opacity-30 text-gray-100 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    placeholder="2162"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={isSubmitting}
                className={`px-8 py-3 rounded-xl font-bold transition-all duration-200 ${
                  isSubmitting
                    ? 'bg-slate-600 text-gray-400 cursor-not-allowed'
                    : 'bg-teal-500 text-slate-900 hover:bg-teal-400 shadow-lg hover:shadow-teal-500/50'
                }`}
              >
                {isSubmitting ? 'Adding...' : 'Add Test Result'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Admin;
